import React, { useState, useContext, useEffect } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ListGroup,
  ListGroupItem,
  Alert,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import { format } from "date-fns";
import { DataContext } from '../context/DataContext';
import {
  createAlert,
  createBudget,
  updateBudget,
  createBrand, updateBrand,
  createDistribution, updateDistribution,
  createCategory, updateCategory,
  createUnitOfMeasure, updateUnitOfMeasure,
  createBanner, updateBanner,
  createRegion, updateRegion,
  createStoreType, updateStoreType, // Added store type API functions
  exportOrdersCsv
} from "../context/api";
import { s3ImageUrl } from "../utils/s3Url";
import { API_BASE } from "../config/api";
import placeholder from "assets/img/placeholder.jpg"; // Added placeholder import

// --- Helper Functions ---
const generateId = () => Date.now().toString();

// Function to convert images to PNG (adapted from Products.js)
function convertToPng(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type) {
      return reject(new Error("Invalid file object."));
    }
    const isSupportedType = /^image\/(jpeg|jpg|png)$/i.test(file.type);
    if (!isSupportedType) {
      return reject(new Error("File type not supported. Please upload JPG or PNG."));
    }
    if (/^image\/png$/i.test(file.type)) { // If already PNG
      return resolve(file);
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error("Canvas to Blob conversion failed"));
            }
            const pngFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '') + '.png',
              { type: 'image/png' }
            );
            resolve(pngFile);
          },
          'image/png'
        );
      };
      img.onerror = (err) => reject(new Error(`Image load error: ${err.message || err}`));
      img.src = reader.result;
    };
    reader.onerror = (err) => reject(new Error(`File reader error: ${err.message || err}`));
    reader.readAsDataURL(file);
  });
}


// --- 1. System Alerts ---
export const SystemAlertsSettings = () => {
  const { data, setData } = useContext(DataContext);
  // console.log("SystemAlertsSettings data:", data);
  const [alertContent, setAlertContent] = useState('');
  const [targetUsers, setTargetUsers] = useState([]); // Array of user IDs
  const [targetType, setTargetType] = useState('everyone'); // 'everyone' or 'specific'
  const [status, setStatus] = useState({ message: '', color: '' });

  const userOptions = data.users.map(user => ({ value: user.id, label: user.name }));

const handleSaveAlert = async () => {
  if (!alertContent) {
    setStatus({ message: 'Alert content cannot be empty.', color: 'danger' });
    return;
  }

  const tempId   = generateId();
  const timestamp= new Date().toISOString();

  // 1) Optimistically insert one “aggregate” alert in the UI
  const newAlert = {
    id:        tempId,
    content:   alertContent,
    targetType,
    targets:   targetType === 'specific' ? targetUsers.map(u => u.value) : [],
    createdAt: timestamp,
    isRead:    false,
  };
  setData(prev => ({
    ...prev,
    settings: {
      ...prev.settings,
      systemAlerts: [...(prev.settings.systemAlerts||[]), newAlert]
    }
  }));

  // 2) clear form
  setAlertContent('');
  setTargetUsers([]);
  setTargetType('everyone');

  // 3) Now actually persist:
  try {
    if (targetType === 'everyone') {
      // fire one POST per user
      await Promise.all(
        data.users.map(u =>
          createAlert({
            userId:    u.id,
            message:   alertContent,
            alertType: 'everyone',
            isRead:    false,
            createdAt: timestamp
          })
        )
      );
    } else {
      // only the selected ones
      await Promise.all(
        targetUsers.map(u =>
          createAlert({
            userId:    u.value,
            message:   alertContent,
            alertType: 'specific',
            isRead:    false,
            createdAt: timestamp
          })
        )
      );
    }

    // if you want, you can now fetch the real alerts back or just append placeholders
    setStatus({ message: 'System alert created!', color: 'success' });
  } catch (err) {
    console.error("Failed to create alert:", err);
    // rollback
    setData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        systemAlerts: (prev.settings.systemAlerts||[]).filter(a => a.id !== tempId)
      }
    }));
    setStatus({ message: 'Failed to create system alert. Please try again.', color: 'danger' });
    
  }
};







  return (
    <Card>
      <CardHeader><h5 className="title">Create System Alert</h5></CardHeader>
            {/* ← your in-page status banner */}
      {status.message && (
        <Alert color={status.color} toggle={() => setStatus({ message:'', color:'' })}>
          {status.message}
        </Alert>
      )}
      <CardBody>
        <FormGroup>
          <Label for="alertContent">Alert Content</Label>
          <Input 
            type="textarea" 
            id="alertContent" 
            value={alertContent} 
            onChange={e => setAlertContent(e.target.value)} 
            placeholder="Enter alert message..."
          />
        </FormGroup>
        <FormGroup>
          <Label for="targetType">Target Users</Label>
          <Input type="select" id="targetType" value={targetType} onChange={e => setTargetType(e.target.value)}>
            <option value="everyone">Everyone</option>
            <option value="specific">Specific Users</option>
          </Input>
        </FormGroup>
        {targetType === 'specific' && (
          <FormGroup>
            <Label for="specificUsers">Select Users</Label>
            <Select
              isMulti
              id="specificUsers"
              options={userOptions}
              value={targetUsers}
              onChange={setTargetUsers}
              placeholder="Select users..."
            />
          </FormGroup>
        )}
      </CardBody>
      <CardFooter>
        <Button color="primary" onClick={handleSaveAlert}>Save Alert</Button>
      </CardFooter>
    </Card>
  );
};

// --- 2. Budget Targets ---
export const BudgetTargetsSettings = () => {
  const { data, setData } = useContext(DataContext);

  // Helper function to format currency for display
  const formatCurrency = (amount) => {
    if (!amount || amount === '') return '';
    const num = parseFloat(amount);
    if (isNaN(num)) return '';
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [status, setStatus] = useState({ message: '', color: '' });
  // yearMode: 'existing' vs 'new'
  const [yearMode, setYearMode] = useState('existing');
  const [selectedYear, setSelectedYear] = useState(null);
  const [newYearInput, setNewYearInput] = useState(new Date().getFullYear());
  const [isSaving, setIsSaving] = useState(false); // <-- Add loading state

  // Build years dropdown whenever territory changes
 const existingYearOptions = React.useMemo(() => {
   if (!selectedTerritory) return [];
   const yrs = Array.from(new Set(
     (data.budgets || [])
       .filter(b => b.territory_id === selectedTerritory.value)
       .map(b => b.year)
   )).sort((a,b) => b - a);
   return yrs.map(y => ({ value: y, label: String(y) }));
 }, [selectedTerritory, data.budgets]);

  // Combined options: existing + “Add new…”
  const yearOptions = [
    ...existingYearOptions,
    { value: '__new', label: 'Add new year…' }
  ];

  // Whenever you switch territory, reset year selection
  useEffect(() => {
    setYearMode('existing');
    setSelectedYear(null);
  }, [selectedTerritory]);

  // Derive the “active” year used for your grid
 const activeYear =
   yearMode === 'new'
     ? newYearInput
     : selectedYear;


  // One entry per month April→March
  const monthNames = [
    'April','May','June','July','August','September',
    'October','November','December','January','February','March'
  ];
  const [monthlyBudgets, setMonthlyBudgets] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingFields, setEditingFields] = useState({}); // Track which fields are being edited

  const territoryOptions = data.territories.map(t => ({
    value: t.id, label: t.name
  }));

  // Whenever territory or year changes, initialize the 12-month grid
  useEffect(() => {
    if (!selectedTerritory || !activeYear) return;
    const existing = data.budgets || [];
    const newGrid = monthNames.map((name, i) => {
      const month = i + 1;           // DB month: 1=April, … 12=March
      const found = existing.find(b =>
        b.territory_id === selectedTerritory.value &&
        b.year === activeYear &&
        b.month === month
      );
      return {
        month,
        monthName: name,
        amount: found ? String(found.amount) : '',
        id:      found ? found.id : null,
      };
    });

    setMonthlyBudgets(newGrid);
    setIsDirty(false);
    setEditingFields({}); // Reset editing state when data changes
  }, [selectedTerritory, activeYear, data.budgets]);

  // Track changes
  const handleAmountChange = (idx, val) => {
    const copy = [...monthlyBudgets];
    copy[idx].amount = val;
    setMonthlyBudgets(copy);
    setIsDirty(true);
  };

  // Handle entering edit mode for a field
  const handleFieldFocus = (idx) => {
    setEditingFields(prev => ({ ...prev, [idx]: true }));
  };

  // Handle leaving edit mode for a field
  const handleFieldBlur = (idx) => {
    setEditingFields(prev => ({ ...prev, [idx]: false }));
  };

  // Get display value for input field (formatted when not editing, raw when editing)
  const getDisplayValue = (amount, idx) => {
    const isEditing = editingFields[idx];
    if (isEditing || !amount || amount === '') {
      return amount; // Show raw value when editing or empty
    }
    return formatCurrency(amount); // Show formatted value when not editing
  };

  // Save: create or update each month as needed
  const handleSave = async () => {
    setIsSaving(true); // <-- Set loading true
    setStatus({ message: '', color: '' }); // Clear previous status

    try {
      const results = [];
      for (const b of monthlyBudgets) {
        const currentParsedAmount = parseFloat(b.amount || '0');
        const apiPayload = {
          territoryId: selectedTerritory.value,
          year:        activeYear,
          month:       b.month,
          amount:      currentParsedAmount,
        };

        let resultForItem;

        if (b.id) { // Existing budget
          const originalBudgetInContext = (data.budgets || []).find(
            dbBudget => dbBudget.id === b.id
          );

          if (originalBudgetInContext && parseFloat(originalBudgetInContext.amount) === currentParsedAmount) {
            resultForItem = originalBudgetInContext;
          } else {
            await updateBudget(b.id, apiPayload); // Wait for the update
            resultForItem = {
              id: b.id,
              territory_id: selectedTerritory.value,
              year: activeYear,
              month: b.month,
              amount: currentParsedAmount,
            };
          }
        } else { // New budget
          if (currentParsedAmount > 0) { // Only create if amount is not zero
            const newlyCreatedBudgetFromServer = await createBudget(apiPayload); // Wait for creation
            if (newlyCreatedBudgetFromServer && typeof newlyCreatedBudgetFromServer.id !== 'undefined') {
              resultForItem = {
                id: newlyCreatedBudgetFromServer.id,
                territory_id: newlyCreatedBudgetFromServer.territory_id || selectedTerritory.value,
                year: newlyCreatedBudgetFromServer.year || activeYear,
                month: newlyCreatedBudgetFromServer.month || b.month,
                amount: (typeof newlyCreatedBudgetFromServer.amount !== 'undefined') ? parseFloat(newlyCreatedBudgetFromServer.amount) : currentParsedAmount,
              };
            } else {
              console.error("createBudget API did not return a valid budget object:", newlyCreatedBudgetFromServer);
              // Decide if you want to throw an error here or just skip this item
              // For now, let's skip and it won't be added to results.
              resultForItem = null; 
            }
          } else {
            // If it's a new budget and amount is 0, don't create it, don't add to results.
            resultForItem = null;
          }
        }
        
        if (resultForItem) {
            results.push(resultForItem);
        }

        // Add a delay after processing each item, except possibly the last one
        if (monthlyBudgets.indexOf(b) < monthlyBudgets.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
        }
      }

      setData(prev => {
        const keptBudgets = (prev.budgets || []).filter(budget =>
          !(budget.territory_id === selectedTerritory.value && budget.year === activeYear)
        );
        return {
          ...prev,
          budgets: [...keptBudgets, ...results.filter(r => r != null)]
        };
      });

      setIsDirty(false);
      setStatus({ message: 'Budget targets saved!', color: 'success' });
    } catch (err) {
      console.error("Error in handleSave:", err);
      setStatus({ message: 'Failed to save budgets – please try again.', color: 'danger' });
    } finally {
      setIsSaving(false); // <-- Set loading false in finally block
    }
  };

  return (
    <Card>
      <CardHeader>
        <h5>Set Budget Targets</h5>
        {status.message && (
          <Alert
            color={status.color}
            toggle={() => setStatus({ message: '', color: '' })}
            className="mt-2 mb-0"
          >
            {status.message}
          </Alert>
        )}
      </CardHeader>
      <CardBody>

        {/* 1) Territory */}
        <FormGroup>
          <Label>Territory</Label>
          <Select
            options={data.territories.map(t => ({
              value: t.id, label: t.name
            }))}
            value={selectedTerritory}
            onChange={t => setSelectedTerritory(t)}
            placeholder="Select Territory…"
          />
        </FormGroup>

        {/* 2) Year dropdown + “Add new” */}
        {selectedTerritory && (
          <FormGroup>
            <Label>Year</Label>
            {yearMode === 'existing' ? (
              <Select
                options={yearOptions.map(opt => 
                  opt.value === '__new' 
                    ? opt 
                    : { ...opt, label: `FY${String(opt.value - 1).slice(-2)}/${String(opt.value).slice(-2)}` }
                )}
                value={
                  selectedYear
                    ? { value: selectedYear, label: `FY${String(selectedYear - 1).slice(-2)}/${String(selectedYear).slice(-2)}` }
                    : null
                }
                onChange={opt => {
                  if (opt.value === '__new') {
                    setYearMode('new');
                    setNewYearInput(new Date().getFullYear());
                  } else {
                    setSelectedYear(opt.value);
                  }
                }}
                placeholder="Pick an existing year or add new…"
              />
            ) : (
              <Input
                type="number"
                min="2000" max="2100"
                value={newYearInput}
                onChange={e => setNewYearInput(parseInt(e.target.value,10))}
                onBlur={() => setSelectedYear(newYearInput)}
              />
            )}
          </FormGroup>
        )}

        {/* 3. Monthly grid April→March */}
        {selectedTerritory && activeYear && (
          <Table bordered>
            <thead>
              <tr>
                <th>Month</th>
                <th>Target Amount</th>
              </tr>
            </thead>
            <tbody>
              {monthlyBudgets.map((b, idx) => (
                <tr key={b.month}>
                  <td>{b.monthName}</td>
                  <td>
                    <Input
                      type={editingFields[idx] ? "number" : "text"}
                      step="100"
                      placeholder="$0"
                      value={getDisplayValue(b.amount, idx)}
                      onChange={e => handleAmountChange(idx, e.target.value)}
                      onFocus={() => handleFieldFocus(idx)}
                      onBlur={() => handleFieldBlur(idx)}
                      style={{ 
                        fontFamily: editingFields[idx] ? 'inherit' : 'monospace',
                        textAlign: 'left'
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>

      {/* 4. Save button only when dirty */}
      <CardFooter>
        <Button
          color="primary"
          disabled={!isDirty || isSaving} // <-- Disable button when saving
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : 'Save Targets'} {/* <-- Change button text when saving */}
        </Button>
      </CardFooter>
    </Card>
  );
};

// src/components/CrudItemModal.js (or wherever it lives)

export const CrudItemModal = ({ isOpen, toggle, item, onSave, itemTypeLabel, fields, settingKey }) => {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({}); // State for validation errors
  const { data: contextData } = useContext(DataContext); // Access DataContext
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(placeholder); // Default to imported placeholder

  useEffect(() => {
    setFormErrors({});
    setSelectedImageFile(null); // Reset selected file
    
    const imageField = fields.find(f => f.type === 'image');
    const imageFieldName = imageField ? imageField.name : null;
    let initialImagePreview = placeholder;
    let initialFormImageValue = '';

    if (item) {
      const initialFormData = {};
      fields.forEach(f => {
        if (f.name === imageFieldName) {
          if (item[f.name] && typeof item[f.name] === 'string' && item[f.name].startsWith('http')) {
            initialImagePreview = `${item[f.name].split('?')[0]}?timestamp=${new Date().getTime()}`;
            initialFormImageValue = initialImagePreview;
          } else if (item.id) {
            initialImagePreview = `${s3ImageUrl(settingKey || 'banners', item.id, ".png")}?timestamp=${new Date().getTime()}`;
            initialFormImageValue = initialImagePreview;
          } // else, initialImagePreview remains placeholder, initialFormImageValue remains ''
          initialFormData[f.name] = initialFormImageValue;
        } else if (f.type === 'select' && f.optionsKey && item[f.name] != null) {
          const itemName = item[f.name];
          const options = contextData[f.optionsKey] || [];
          const foundOption = options.find(opt => opt.name === itemName);
          initialFormData[f.name] = foundOption ? foundOption.id : null;
        } else {
          initialFormData[f.name] = item[f.name] == null 
            ? (f.type === 'checkbox' ? false : (f.type === 'select' ? null : '')) 
            : item[f.name];
        }
      });
      initialFormData.id = item.id; // Copy existing item ID
      initialFormData.is_archived = !!item.is_archived; // Ensure isArchived is a boolean
      setFormData(initialFormData);
      setImagePreviewUrl(initialImagePreview);
    } else { // New item
      const initial = {};
      fields.forEach(f => {
        initial[f.name] = f.type === 'checkbox' ? false : (f.type === 'select' ? null : (f.type === 'image' ? '' : ''));
      });
      initial.id = null; // No ID for new items yet
      initial.is_archived = false;
      setFormData(initial);
      if (imageFieldName) {
        setImagePreviewUrl(placeholder);
      }
    }
  }, [item, fields, isOpen, contextData, settingKey]); // Added settingKey to dependencies

  const handleImageChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedImageFile(null);
      let originalImageUrl = placeholder;
      if (item && item[fieldName] && item[fieldName].startsWith('http')) {
        originalImageUrl = `${item[fieldName].split('?')[0]}?timestamp=${new Date().getTime()}`;
      } else if (item && item.id) {
        originalImageUrl = `${s3ImageUrl(settingKey || 'banners', item.id, ".png")}?timestamp=${new Date().getTime()}`;
      }
      setImagePreviewUrl(originalImageUrl);
      setFormData(prev => ({ ...prev, [fieldName]: originalImageUrl === placeholder ? '' : originalImageUrl }));
      return;
    }

    try {
      const pngFile = await convertToPng(file);
      setSelectedImageFile(pngFile);
      const blobUrl = URL.createObjectURL(pngFile);
      setImagePreviewUrl(blobUrl);
      setFormData(prev => ({ ...prev, [fieldName]: blobUrl }));
    } catch (err) {
      console.error("Image processing failed:", err);
      alert(`Image processing error: ${err.message}`);
      setSelectedImageFile(null);
      let originalImageUrl = placeholder;
      if (item && item[fieldName] && item[fieldName].startsWith('http')) {
        originalImageUrl = `${item[fieldName].split('?')[0]}?timestamp=${new Date().getTime()}`;
      } else if (item && item.id) {
        originalImageUrl = `${s3ImageUrl(settingKey || 'banners', item.id, ".png")}?timestamp=${new Date().getTime()}`;
      }
      setImagePreviewUrl(originalImageUrl);
      setFormData(prev => ({ ...prev, [fieldName]: originalImageUrl === placeholder ? '' : originalImageUrl }));
    }
  };

  const handleChange = e => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear validation error for the field being changed
    if (formErrors[name]) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const nameField = fields.find(f => f.name === 'name');
    if (nameField && (!formData.name || formData.name.trim() === '')) {
      errors.name = 'Name is required.';
    }
    // Add other field validations here if needed
    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData, selectedImageFile); // Pass selectedImageFile
    } else {
      console.log("Validation failed", formErrors);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg"> 
      <ModalHeader toggle={toggle} className="bg-light text-dark">
        { item ? `Edit ${itemTypeLabel}` : `Add New ${itemTypeLabel}` }
      </ModalHeader>
      <ModalBody className="py-4"> 
        {fields.map(f => {
          if (f.type === 'image') {
            return (
              <FormGroup key={f.name} className="mb-3">
                <Label for={f.name} className="font-weight-bold">{f.label}</Label>
                <div>
                  <img 
                    src={imagePreviewUrl || placeholder} // Use imported placeholder
                    alt="Preview" 
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '10px', border: '1px solid #ddd' }}
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholder; }} // Fallback to imported placeholder
                  />
                </div>
                <Input
                  type="file"
                  name={f.name}
                  id={f.name}
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, f.name)}
                  bsSize="sm"
                />
              </FormGroup>
            );
          } else if (f.type === 'select' && f.optionsKey) {
            const selectOptions = (contextData[f.optionsKey] || []).map(opt => ({
              value: opt.id, // Assuming your options have id and name
              label: opt.name
            }));
            const currentValue = selectOptions.find(opt => opt.value === formData[f.name]);

            // Custom styles for react-select to ensure dropdown is on top
            const customSelectStyles = {
              menu: (provided) => ({
                ...provided,
                zIndex: 9999 // High z-index to ensure dropdown is on top
              })
            };

            return (
              <FormGroup key={f.name} className="mb-3">
                <Label for={f.name} className="font-weight-bold">{f.label}</Label>
                <Select
                  id={f.name}
                  name={f.name}
                  options={selectOptions}
                  value={currentValue}
                  onChange={selectedOption => {
                    setFormData(prev => ({
                      ...prev,
                      [f.name]: selectedOption ? selectedOption.value : null
                    }));
                  }}
                  placeholder={`Select ${f.label}...`}
                  isClearable
                  classNamePrefix="react-select"
                  styles={customSelectStyles} // Apply custom styles
                />
              </FormGroup>
            );
          } 
          // Handle checkbox type fields
          else if (f.type === 'checkbox') {
            return (
              <FormGroup key={f.name} className="mb-3">
                 <Label for={f.name} className="font-weight-bold">{f.label}</Label>
                <div className="custom-control custom-switch">
                  <Input
                    type="checkbox"
                    name={f.name}
                    id={f.name}
                    className="custom-control-input"
                    checked={!!formData[f.name]}
                    onChange={handleChange}
                  />
                  <Label className="custom-control-label" for={f.name}></Label>
                </div>
              </FormGroup>
            );
          } 
          // Default to text input
          else {
            return (
              <FormGroup key={f.name} className="mb-3">
                <Label for={f.name} className="font-weight-bold">{f.label}</Label>
                <Input
                  type={f.type || 'text'}
                  name={f.name}
                  id={f.name}
                  value={formData[f.name] || ''}
                  onChange={handleChange}
                  bsSize="lg"
                  placeholder={`Enter ${f.label}...`}
                  invalid={!!formErrors[f.name]} // Add invalid prop based on error state
                />
                {/* Display error message if it exists for this field */}
                {formErrors[f.name] && (
                  <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>
                    {formErrors[f.name]}
                  </div>
                )}
              </FormGroup>
            );
          }
        })}

        {item && (
          <FormGroup className="mt-4">
            <Label className="font-weight-bold">Status</Label>
            <div className="custom-control custom-switch">
              <Input
                type="checkbox"
                name="is_archived"
                id="is_archivedSwitch" // Ensure unique ID for the switch
                className="custom-control-input"
                checked={!!formData.is_archived}
                onChange={handleChange}
              />
              <Label className="custom-control-label" for="is_archivedSwitch">
                {formData.is_archived ? "Archived" : "Active"}
              </Label>
            </div>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button color="primary" onClick={handleSave}>Save {itemTypeLabel}</Button>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
};


// --- Generic Management Component for Banners, Distributions, etc. ---
export const ManageItemsSettings = ({ itemTypeLabel, settingKey, fields, itemApiFunctions }) => {
  const { data, setData } = useContext(DataContext);
  const rawItems = data[settingKey] || [];
  const items = React.useMemo(() => {
    const seen = new Set();
    return rawItems.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [rawItems]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [status, setStatus] = useState({ message: '', type: '' });

  const openModalForNew = () => {
    setEditingItem(null);
    setStatus({ message: '', type: '' }); // Clear previous status
    setModalOpen(true);
  };

  const openModalForEdit = (itemToEdit) => {
    setEditingItem(itemToEdit);
    setStatus({ message: '', type: '' }); // Clear previous status
    setModalOpen(true);
  };

  const closeModal = () => {
    setEditingItem(null); 
    setModalOpen(false);
  };

  // Adapted S3 Upload function
  async function uploadItemImageToS3(file, itemId, folder) {
    const API = API_BASE;
    const presignedUrlPayload = {
      fileType: file.type,
      folder: folder, // e.g., 'banners'
      userId: String(itemId) 
    };

    const res = await fetch(`${API}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(presignedUrlPayload),
    });

    if (!res.ok) {
      let backendMessage = '';
      try { const errorResponse = await res.json(); backendMessage = errorResponse.message || ''; }
      catch (e) { try { const textError = await res.text(); if (textError) backendMessage = textError; } catch (textE) {} }
      const statusText = res.statusText ? ` ${res.statusText}` : '';
      throw new Error(`Failed to get upload URL: ${backendMessage || `HTTP error ${res.status}${statusText}`}`);
    }
    
    const { uploadUrl, key: returnedKey } = await res.json();
    const uploadRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      const statusText = uploadRes.statusText ? ` ${uploadRes.statusText}` : '';
      throw new Error(`Failed to upload file to S3: ${errorText || `HTTP error ${uploadRes.status}${statusText}`}`);
    }
    const bucket = process.env.REACT_APP_S3_BUCKET;
    const region = process.env.REACT_APP_AWS_REGION;
    if (!bucket || !region) throw new Error("S3 configuration missing.");
    return `https://${bucket}.s3.${region}.amazonaws.com/${returnedKey}`;
  }


  const handleSaveItem = async (itemDataFromModal, selectedImageFile) => {
    setStatus({ message: 'Saving...', type: 'info' });
    let apiPayload = { ...itemDataFromModal };
    const imageField = fields.find(f => f.type === 'image');
    const imageFieldName = imageField ? imageField.name : null;

    // If image field exists, ensure its value in apiPayload is handled correctly for S3
    if (imageFieldName) {
      if (typeof apiPayload[imageFieldName] === 'string' && apiPayload[imageFieldName].startsWith('blob:')) {
        // If it's a blob, it means a new image was previewed but might not be the one to save (if selectedImageFile is null)
        // Or selectedImageFile will be uploaded. So, remove blob from payload; S3 URL will be set later.
        delete apiPayload[imageFieldName];
      }
    }

    fields.forEach(f => {
      if (f.type === 'select' && f.optionsKey && apiPayload[f.name] != null) {
        const options = data[f.optionsKey] || [];
        const selectedOption = options.find(opt => opt.id == apiPayload[f.name]);
        if (selectedOption) apiPayload[f.name] = selectedOption.name;
      }
    });

    try {
      let savedItemFromServer;
      let s3PictureUrl = null;

      if (apiPayload.id) { // Existing item
        const originalItemFromContext = items.find(i => i.id === apiPayload.id);

        if (selectedImageFile && imageFieldName) {
          s3PictureUrl = await uploadItemImageToS3(selectedImageFile, apiPayload.id, settingKey || 'banners');
          apiPayload[imageFieldName] = `${s3PictureUrl}?timestamp=${new Date().getTime()}`;
        } else if (imageFieldName) {
          // No new file selected. Decide what to do with the existing image URL.
          const formImageUrl = itemDataFromModal[imageFieldName];
          if (formImageUrl && formImageUrl.startsWith('http')) {
            // Form has an S3 URL (likely from initial load, possibly re-timestamped by modal open).
            // Re-timestamp it again for this save operation to be sure.
            apiPayload[imageFieldName] = `${formImageUrl.split('?')[0]}?timestamp=${new Date().getTime()}`;
          } else if (originalItemFromContext && originalItemFromContext[imageFieldName] && originalItemFromContext[imageFieldName].startsWith('http')) {
            // Form URL is not S3 (e.g., placeholder, blob from cancelled selection).
            // Revert to the original item's S3 URL from context, and re-timestamp it.
            apiPayload[imageFieldName] = `${originalItemFromContext[imageFieldName].split('?')[0]}?timestamp=${new Date().getTime()}`;
          } else {
            // No new file, form doesn't have S3 URL, and original item also doesn't have S3 URL.
            // Ensure the field is removed or set to null if backend expects that for empty images.
            delete apiPayload[imageFieldName]; 
            // Or: apiPayload[imageFieldName] = null;
          }
        }
        const { id, ...updateData } = apiPayload;
        savedItemFromServer = await itemApiFunctions.update(id, updateData);
      } else { // New item
        // Create item first (potentially without image URL)
        const { id, ...createData } = apiPayload;
        if (imageFieldName) delete createData[imageFieldName]; // Don't send blob or empty string for image on create
        
        savedItemFromServer = await itemApiFunctions.create(createData);

        if (selectedImageFile && imageFieldName && savedItemFromServer.id) {
          s3PictureUrl = await uploadItemImageToS3(selectedImageFile, savedItemFromServer.id, settingKey || 'banners');
          const imageUpdatePayload = { [imageFieldName]: `${s3PictureUrl}?timestamp=${new Date().getTime()}` };
          // Update the item with the new image URL
          savedItemFromServer = await itemApiFunctions.update(savedItemFromServer.id, imageUpdatePayload);
        }
      }

      let itemForContext = { ...savedItemFromServer };
      if (apiPayload.id) itemForContext.id = apiPayload.id;
      else if (savedItemFromServer && savedItemFromServer.id) itemForContext.id = savedItemFromServer.id;
      
      if (imageFieldName && savedItemFromServer[imageFieldName]) {
         itemForContext[imageFieldName] = savedItemFromServer[imageFieldName]; // Ensure this is the (timestamped) S3 URL
      } else if (imageFieldName && s3PictureUrl) { // Fallback if API didn't return the image URL after update
         itemForContext[imageFieldName] = `${s3PictureUrl}?timestamp=${new Date().getTime()}`;
      }


      fields.forEach(f => {
        if (f.type === 'select' && f.optionsKey) {
          const valueFromApi = savedItemFromServer[f.name];
          const options = data[f.optionsKey] || [];
          const optionMatchingApiValueAsId = options.find(opt => opt.id === valueFromApi);

          if (optionMatchingApiValueAsId) {
            // API returned an ID, convert it to name for context
            itemForContext[f.name] = optionMatchingApiValueAsId.name;
          } else {
            // API likely returned the name directly, or it's a value not matching any ID.
            // Assume valueFromApi is the correct name (or null/undefined) to store in context.
            itemForContext[f.name] = valueFromApi;
          }
        } else if (f.type === 'checkbox') {
          // For checkboxes, trust the value that was submitted in the form (from apiPayload),
          itemForContext[f.name] = !!apiPayload[f.name];
        }
        // For other field types, the value from { ...savedItemFromServer } is used by default.
      });

      // Specifically handle 'is_archived' (if managed by CrudItemModal's own switch)
      if (apiPayload.hasOwnProperty('is_archived')) {
        itemForContext.is_archived = !!apiPayload.is_archived;
      }

      // Update the DataContext
      if (apiPayload.id) { // Item was updated
        setData(prev => ({
          ...prev,
          [settingKey]: prev[settingKey].map(i => (i.id === apiPayload.id ? itemForContext : i)),
        }));
      } else { // Item was created
        setData(prev => ({
          ...prev,
          [settingKey]: [...(prev[settingKey] || []), itemForContext],
        }));
      }

      setStatus({ message: `${itemTypeLabel} saved successfully!`, type: 'success' });
      closeModal(); // Close modal on success

    } catch (error) {
      console.error(`Failed to save ${itemTypeLabel}:`, error);
      let errorMessage = error.message || 'Please try again.';
      // Attempt to get a more specific error message if the API provides one (Axios example)
      if (error.response && error.response.data && typeof error.response.data.message === 'string') {
        errorMessage = error.response.data.message;
      } else if (typeof error.message === 'string') {
        errorMessage = error.message;
      }
      setStatus({ message: `Failed to save ${itemTypeLabel}. ${errorMessage}`, type: 'danger' });
      // Modal remains open on error for correction.
    }
  };

  return (
    <Card>
      <CardHeader>
        <h5 className="title">{itemTypeLabel}</h5>
      </CardHeader>
      <CardBody>
        {status.message && (
          <Alert color={status.type} isOpen={!!status.message} toggle={() => setStatus({ message: '', type: '' })} className="mb-3">
            {status.message}
          </Alert>
        )}
        <Button color="success" size="sm" onClick={openModalForNew} className="mb-3">
          New {itemTypeLabel}
        </Button>

        <ListGroup flush>
          {[...items] 
            .sort((a, b) => {
              const nameA = (a.name || '').toLowerCase();
              const nameB = (b.name || '').toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            })
            .map(i => {
              const isArchived = !!(i.is_archived || i.isArchived);
              const itemStyle = isArchived ? { textDecoration: 'line-through', color: 'grey' } : {};
              
              const imageFieldDef = fields.find(f => f.type === 'image');
              const imageFieldName = imageFieldDef ? imageFieldDef.name : null;
              let itemImageSrc = placeholder; // Default to imported placeholder
              if (imageFieldName && i[imageFieldName] && i[imageFieldName].startsWith('http')) {
                itemImageSrc = i[imageFieldName]; 
              } else if (imageFieldName && i.id) {
                itemImageSrc = `${s3ImageUrl(settingKey || 'banners', i.id, ".png")}?timestamp=${new Date().getTime()}`;
              }

              return (
                <ListGroupItem 
                  key={i.id} 
                  className="d-flex justify-content-between align-items-start"
                  style={itemStyle}
                >
                  {imageFieldName && (
                    <img 
                      src={itemImageSrc}
                      alt={i.name || itemTypeLabel} 
                      style={{ width: '100px', height: 'auto', maxHeight: '100px', objectFit: 'contain', marginRight: '15px', border: '1px solid #eee' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = placeholder;}} // Fallback to imported placeholder
                    />
                  )}
                  <div style={{ flexGrow: 1 }}>
                    {fields
                      .filter(f => f.type !== 'image')
                      .map(f => (
                      <div key={f.name} className="mb-1">
                        <strong>{f.label}:</strong>{' '}
                        {f.type === 'checkbox'
                          ? (i[f.name] ? '✔️' : '—')
                          : (f.type === 'select' && i[f.name] && data[f.optionsKey] 
                              ? (data[f.optionsKey].find(opt => {
                                  // Handle cases where i[f.name] might be an ID (number/string) or a name (string)
                                  return String(opt.id) === String(i[f.name]) || opt.name === i[f.name];
                                })?.name || i[f.name]) 
                              : i[f.name])
                        }
                      </div>
                    ))}
                    {isArchived && ( // Display (Archived) text more clearly if needed, though style handles it
                      <div className="mb-1"><small className="text-muted">(Archived)</small></div>
                    )}
                  </div>
                  <div className="ml-2" style={{minWidth: '80px'}}>
                    <Button size="sm" color="info" onClick={() => openModalForEdit(i)} className="mb-1 d-block w-100">
                      Edit
                    </Button>
                  </div>
                </ListGroupItem>
              );
            })}
          {items.length === 0 && <ListGroupItem>No {itemTypeLabel.toLowerCase()} found.</ListGroupItem>}
        </ListGroup>
      </CardBody>
      <CrudItemModal
        isOpen={modalOpen}
        toggle={closeModal}
        item={editingItem}
        onSave={handleSaveItem}
        itemTypeLabel={itemTypeLabel}
        fields={fields}
        settingKey={settingKey} // Pass settingKey to CrudItemModal
      />
    </Card>
  );
};

// --- 3. Banners ---
export const BannerSettings = () => (
  <ManageItemsSettings
    itemType="Banner"
    itemTypeLabel="Banners"
    settingKey="banners"
    fields={[
      { name: 'name', label: 'Name' },
      { name: 'pictureUrl', label: 'Banner Image', type: 'image' } // Added image field
    ]}
    itemApiFunctions={{ create: createBanner, update: updateBanner }}
  />
);

// --- 4. Distributions ---
export const DistributionSettings = () => (
<ManageItemsSettings
  itemTypeLabel="Distributions"
  itemType="Distribution"
  settingKey="distributions"
  fields={[
    { name: 'name',      label: 'Name',      type: 'text'     },
    { name: 'is_pengelly', label: 'Pengelly',  type: 'checkbox' }
  ]}
  itemApiFunctions={{ create: createDistribution, update: updateDistribution }}
/>
);

// --- 5. Brands ---
export const BrandSettings = () => (
  <ManageItemsSettings
    itemType="Brand"
    itemTypeLabel="Brands"
    settingKey="brands"
    fields={[
      { name: 'name',        label: 'Name'       }, 
      { name: 'category',    label: 'Category', type: 'select', optionsKey: 'categories' },
      { name: 'owner',       label: 'Owner'            },
      { name: 'description', label: 'Description'      }
    ]}
    itemApiFunctions={{ create: createBrand, update: updateBrand }}
  />
);

// --- 6. Product Types --- 
// Assuming ProductTypeSettings manages 'categories'
export const ProductTypeSettings = () => (
  <ManageItemsSettings
    itemType="Category" 
    itemTypeLabel="Categories"
    settingKey="categories"
    fields={[{ name: 'name', label: 'Name' }]}
    itemApiFunctions={{ create: createCategory, update: updateCategory }}
  />
);

// --- 6. Units of Measure ---
export const UnitsOfMeasureSettings = () => (
  <ManageItemsSettings
    itemType="UnitOfMeasure"
    itemTypeLabel="Units Of Measure"
    settingKey="unitsOfMeasure"
    fields={[{ name: 'name', label: 'Name' }]}
    itemApiFunctions={{ create: createUnitOfMeasure, update: updateUnitOfMeasure }}
  />
);


// --- 7. Regions ---
export const RegionSettings = () => (
  <ManageItemsSettings
    itemType="Region"
    itemTypeLabel="Regions"
    settingKey="regions"
    fields={[{ name: 'name', label: 'Region Name' }]}
    itemApiFunctions={{ create: createRegion, update: updateRegion }}
  />
);

// --- Store Types ---
export const StoreTypeSettings = () => (
  <ManageItemsSettings
    itemType="StoreType" 
    itemTypeLabel="Store Types"
    settingKey="storeType" // Matches DataContext key
    fields={[{ name: 'name', label: 'Name' }]}
    itemApiFunctions={{ create: createStoreType, update: updateStoreType }}
  />
);


// --- 8. Bulk Import ---
export const BulkImportSettings = () => {
  const { setData } = useContext(DataContext);
  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState('customers'); // 'orders', 'products', 'customers'
  const [modalOpen, setModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      alert("Please select a .csv file.");
      setFile(null);
      e.target.value = null; // Reset file input
    }
  };

  const confirmImport = () => {
    if (!file) {
      alert("Please select a file to import.");
      return;
    }
    setWarningMessage(`Are you sure you want to import \${importType} from \${file.name}? This may overwrite existing data.`);
    setModalOpen(true);
  };

  const handleImport = () => {
    setModalOpen(false);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const csvData = event.target.result;
      // Basic CSV parsing (can be improved with a library like PapaParse)
      try {
        const lines = csvData.split('\\n').filter(line => line.trim() !== '');
        if (lines.length < 2) throw new Error("CSV file must have a header and at least one data row.");
        
        const header = lines[0].split(',').map(h => h.trim());
        const jsonData = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          return header.reduce((obj, nextKey, index) => {
            obj[nextKey] = values[index];
            return obj;
          }, {});
        });

        setData(prev => {
          // Simple merge: replace existing array with imported.
          // More sophisticated merging (e.g., by ID) would be needed for production.
          let updatedData = {};
          if (importType === 'customers') updatedData = { customers: jsonData };
          else if (importType === 'products') updatedData = { products: jsonData };
          else if (importType === 'orders') updatedData = { orders: jsonData }; // Assuming orders are stored flatly

          return { ...prev, ...updatedData };
        });
        alert(`${importType} imported successfully!`);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        alert(`Error importing CSV: \${error.message}`);
      }
    };
    reader.readAsText(file);
    setFile(null); 
    // Reset the file input visually, find a better way if this doesn't work across browsers
    const fileInput = document.getElementById('bulkImportFile');
    if (fileInput) fileInput.value = null;
  };

  return (
    <Card>
      <CardHeader><h5 className="title">Bulk Import Data</h5></CardHeader>
      <CardBody>
        <FormGroup>
          <Label for="importTypeSelect">Data Type to Import</Label>
          <Input type="select" id="importTypeSelect" value={importType} onChange={e => setImportType(e.target.value)}>
            <option value="customers">Customers</option>
            <option value="products">Products</option>
            <option value="orders">Orders</option>
          </Input>
        </FormGroup>
        <FormGroup>
          <Label for="bulkImportFile">Upload CSV File</Label>
          <Input type="file" id="bulkImportFile" accept=".csv" onChange={handleFileChange} />
        </FormGroup>
      </CardBody>
      <CardFooter>
        <Button color="primary" onClick={confirmImport} disabled={!file}>Import Data</Button>
      </CardFooter>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>Confirm Import</ModalHeader>
        <ModalBody>{warningMessage}</ModalBody>
        <ModalFooter>
          <Button color="warning" onClick={handleImport}>Confirm & Import</Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

// --- 9. Download Data ---
export const DownloadDataSettings = () => {
  const { data } = useContext(DataContext);
  const [ordersStartDate, setOrdersStartDate] = useState(null);
  const [ordersEndDate, setOrdersEndDate] = useState(null);
  const [exportingOrders, setExportingOrders] = useState(false);
  const [ordersStatus, setOrdersStatus] = useState({ message: '', color: '' });

  const convertToCSV = (dataArray, headers) => {
    if (!Array.isArray(dataArray) || dataArray.length === 0) return '';
    const cols = headers || Object.keys(dataArray[0]);
    const headerRow = cols.join(',');
    const rows = dataArray.map((item) =>
      cols
        .map((key) => {
          let val = item[key] == null ? '' : item[key];

          if (typeof val === 'object' && val !== null) {
            val = JSON.stringify(val);
          } else {
            val = String(val);
          }

          val = val.replace(/"/g, '""');
          return `"${val}"`;
        })
        .join(',')
    );

    return [headerRow, ...rows].join('\r\n');
  };

  const downloadCSV = (dataType) => {
    let csvData;
    let fileName = `${dataType}.csv`;

    switch (dataType) {
      case 'customers':
        // Define specific headers for customers if needed, or derive from data
        // Example: headers = ['id', 'name', 'email', 'storeType', ...];
        csvData = convertToCSV(data.customers);
        break;
      case 'products':
        csvData = convertToCSV(data.products);
        break;
      default:
        alert("Unknown data type for download.");
        return;
    }

    if (!csvData) {
        alert(`No ${dataType} data available to download.`);
        return;
    }

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOrdersExport = async () => {
    if (!ordersStartDate || !ordersEndDate) {
      setOrdersStatus({ message: 'Select both start and end dates.', color: 'danger' });
      return;
    }
    if (ordersStartDate > ordersEndDate) {
      setOrdersStatus({ message: 'Start date cannot be after end date.', color: 'danger' });
      return;
    }

    const startStr = format(ordersStartDate, "yyyy-MM-dd");
    const endStr = format(ordersEndDate, "yyyy-MM-dd");

    setExportingOrders(true);
    setOrdersStatus({ message: '', color: '' });

    try {
      const { blob, filename } = await exportOrdersCsv(startStr, endStr);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `orders_${startStr}_${endStr}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setOrdersStatus({
        message: `Downloaded ${filename || `orders_${startStr}_${endStr}.csv`}`,
        color: 'success'
      });
    } catch (err) {
      console.error("Failed to export orders CSV:", err);
      setOrdersStatus({
        message: err.message || "Failed to export orders CSV.",
        color: 'danger'
      });
    } finally {
      setExportingOrders(false);
    }
  };

  return (
    <Card>
      <CardHeader><h5 className="title">Download Data</h5></CardHeader>
      <CardBody>
        <p>Download data exports for analysis and backups.</p>
        <Row className="mb-3">
          <Col md="6">
            <Button block color="info" onClick={() => downloadCSV('customers')}>Download Customers CSV</Button>
          </Col>
          <Col md="6">
            <Button block color="info" onClick={() => downloadCSV('products')}>Download Products CSV</Button>
          </Col>
        </Row>
        <hr />
        <h6 className="mb-3">Orders</h6>
        {ordersStatus.message && (
          <Alert color={ordersStatus.color} toggle={() => setOrdersStatus({ message: '', color: '' })}>
            {ordersStatus.message}
          </Alert>
        )}
        <Row>
          <Col md="4">
            <FormGroup>
              <Label>Start date</Label>
              <DatePicker
                selected={ordersStartDate}
                onChange={(date) => setOrdersStartDate(date)}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                placeholderText="YYYY-MM-DD"
                maxDate={ordersEndDate || undefined}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>End date</Label>
              <DatePicker
                selected={ordersEndDate}
                onChange={(date) => setOrdersEndDate(date)}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                placeholderText="YYYY-MM-DD"
                minDate={ordersStartDate || undefined}
              />
            </FormGroup>
          </Col>
          <Col md="4" className="d-flex align-items-end">
            <Button
              block
              color="primary"
              onClick={handleOrdersExport}
              disabled={exportingOrders || !ordersStartDate || !ordersEndDate}
            >
              {exportingOrders ? "Downloading..." : "Download Orders CSV"}
            </Button>
          </Col>
        </Row>
        {/* <p className="text-muted mt-2 mb-0">
          Dates must be in YYYY-MM-DD format and are inclusive.
        </p> */}
      </CardBody>
    </Card>
  );
};
